import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useClassifier } from "@/hooks/useClassifier";
import { toast } from "sonner";
import { useLlmModels } from "@/hooks/useLlmModels";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { LlmModelKey } from "@smartmed/llm-models";
import React from "react";

interface ReClassifyDialogProps {
  open: boolean;
  onClose: () => void;
  testDataId: string;
  onReClassifyComplete?: () => void;
}

export function ReClassifyDialog({ open, onClose, testDataId, onReClassifyComplete }: ReClassifyDialogProps) {
  const [selectedModelId, setSelectedModelId] = useState<LlmModelKey | "">("");
  const [isClassifying, setIsClassifying] = useState(false);
  const { classifyTestData } = useClassifier();
  const { data: llmModels, isLoading: isLlmModelsLoading, error: llmModelsError } = useLlmModels();

  // Set a default model if available
  React.useEffect(() => {
    if (llmModels && llmModels.length > 0 && !selectedModelId) {
      setSelectedModelId(llmModels[0].id);
    }
  }, [llmModels]);

  React.useEffect(() => {
    if (llmModelsError) {
      toast.error("Failed to fetch LLM models");
    }
  }, [llmModelsError, toast]);

  const handleReClassify = async () => {
    if (!selectedModelId) {
      toast.error("Please select an LLM model");
      return;
    }

    setIsClassifying(true);
    try {
      const result = await classifyTestData(testDataId, selectedModelId);

      if (result.success) {
        toast.success("Test data has been re-classified successfully");
        onReClassifyComplete?.();
        onClose();
      } else {
        toast.error(result.error || "Failed to re-classify test data");
      }
    } catch (error) {
      console.error("Error re-classifying test data:", error);
      toast.error(error instanceof Error ? error.message : "Failed to re-classify test data");
    } finally {
      setIsClassifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Re-classify Test Data</DialogTitle>
          <DialogDescription>Select an LLM model to re-classify this test data</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="model-select" className="text-sm font-medium">
              LLM Model
            </label>
            <Select
              value={selectedModelId}
              onValueChange={(value) => setSelectedModelId(value as LlmModelKey)}
              disabled={isLlmModelsLoading || !llmModels || llmModels.length === 0}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    isLlmModelsLoading
                      ? "Loading models..."
                      : llmModels && llmModels.length > 0
                        ? "Select a model"
                        : "No models available..."
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {isLlmModelsLoading ? (
                  <div className="px-4 py-2 text-muted-foreground">Loading...</div>
                ) : llmModels && llmModels.length > 0 ? (
                  llmModels.map((model) => (
                    <SelectItem key={model.id} value={model.id}>
                      {model.name}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-4 py-2 text-muted-foreground">No models available</div>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleReClassify}
              disabled={isClassifying || !selectedModelId || isLlmModelsLoading || !llmModels || llmModels.length === 0}
            >
              {isClassifying ? "Re-classifying..." : "Re-classify"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
